import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { X, Mail, MessageCircle, AlertCircle, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { cliente360Api, api } from "../../lib/api";

type Canal = "email" | "whatsapp";

interface PlantillaDB {
  id: number;
  tipo: string;
  asunto: string;
  cuerpo: string;
  activo: boolean;
}

interface Props {
  open: boolean;
  segmento: "vip" | "enRiesgo";
  codClientes?: number[]; // si querés mandar solo a una sub-lista
  onClose: () => void;
}

// Mapeo del segmento+canal al tipo de plantilla en la BD
function tipoPlantilla(segmento: "vip" | "enRiesgo", canal: Canal): string {
  const seg = segmento === "vip" ? "vip" : "riesgo";
  const ch = canal === "email" ? "email" : "whatsapp";
  return `campana_${seg}_${ch}`;
}

const TIPOS_RELEVANTES = [
  "campana_vip_email", "campana_vip_whatsapp",
  "campana_riesgo_email", "campana_riesgo_whatsapp",
  "campana_cumpleanos_email", "campana_cumpleanos_whatsapp",
  "campana_general_email", "campana_general_whatsapp",
];

const LABEL_TIPO: Record<string, string> = {
  campana_vip_email: "VIP — Email",
  campana_vip_whatsapp: "VIP — WhatsApp",
  campana_riesgo_email: "Re-engagement — Email",
  campana_riesgo_whatsapp: "Re-engagement — WhatsApp",
  campana_cumpleanos_email: "Cumpleaños — Email",
  campana_cumpleanos_whatsapp: "Cumpleaños — WhatsApp",
  campana_general_email: "Mensaje genérico — Email",
  campana_general_whatsapp: "Mensaje genérico — WhatsApp",
};

export function CampanaMasivaModal({ open, segmento, codClientes, onClose }: Props) {
  const [canal, setCanal] = useState<Canal>("email");
  const [plantillaTipo, setPlantillaTipo] = useState<string>("");
  const [asunto, setAsunto] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<null | Awaited<ReturnType<typeof cliente360Api.campanaMasiva>>>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  // Cargar plantillas desde la BD
  const { data: plantillasDB = [] } = useQuery<PlantillaDB[]>({
    queryKey: ["email-templates", "campanas"],
    queryFn: () => api<PlantillaDB[]>("/v1/email-templates"),
    enabled: open,
  });

  // Filtrar solo las relevantes para campañas
  const plantillasFiltradas = plantillasDB
    .filter((p) => TIPOS_RELEVANTES.includes(p.tipo))
    .filter((p) => canal === "email" ? p.tipo.includes("email") : p.tipo.includes("whatsapp"));

  // Auto-seleccionar plantilla según segmento+canal
  useEffect(() => {
    if (!open || plantillasFiltradas.length === 0) return;
    const tipoPreferido = tipoPlantilla(segmento, canal);
    const found = plantillasFiltradas.find((p) => p.tipo === tipoPreferido);
    const p = found ?? plantillasFiltradas[0];
    setPlantillaTipo(p.tipo);
    setAsunto(p.asunto ?? "");
    setCuerpo(p.cuerpo);
    setResultado(null); setError(null);
  }, [open, segmento, canal, plantillasDB.length]);

  if (!open) return null;

  const enviar = async () => {
    setEnviando(true); setError(null); setResultado(null);
    try {
      const r = await cliente360Api.campanaMasiva({
        canal,
        segmento: codClientes && codClientes.length > 0 ? undefined : segmento,
        codClientes: codClientes && codClientes.length > 0 ? codClientes : undefined,
        asunto: canal === "email" ? asunto : undefined,
        cuerpo,
      });
      setResultado(r);
    } catch (e: any) {
      setError(e?.message || "Error al enviar la campaña");
    } finally {
      setEnviando(false);
      setConfirmando(false);
    }
  };

  const titulo = segmento === "vip" ? "Campaña VIP" : "Campaña re-engagement";
  const colorTitulo = segmento === "vip" ? "from-amber-500 to-amber-600" : "from-rose-500 to-rose-600";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className={`bg-gradient-to-r ${colorTitulo} text-white px-5 py-4 rounded-t-2xl flex items-center justify-between`}>
          <div>
            <h2 className="font-bold text-lg">{titulo}</h2>
            <p className="text-sm opacity-90">
              {codClientes && codClientes.length > 0
                ? `${codClientes.length} cliente${codClientes.length === 1 ? "" : "s"} seleccionado${codClientes.length === 1 ? "" : "s"}`
                : "Todo el segmento"}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20 transition">
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
          {resultado ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <CheckCircle2 className="size-14 mx-auto text-emerald-500 mb-2" />
                <h3 className="font-bold text-xl text-gray-900">Campaña enviada</h3>
                <p className="text-gray-500 text-sm">
                  Resultados de la campaña por {canal === "email" ? "email" : "WhatsApp"}.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ResultBox label="Candidatos" value={resultado.totalCandidatos} />
                <ResultBox label="Sin canal" value={resultado.sinCanal} color="text-amber-600" />
                <ResultBox label="Enviados" value={resultado.enviados} color="text-emerald-600" big />
                <ResultBox label="Fallidos" value={resultado.fallidos} color="text-rose-600" />
              </div>
              {resultado.erroresMuestra?.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-rose-700 uppercase mb-2">
                    Muestra de fallos ({resultado.erroresMuestra.length})
                  </p>
                  <ul className="space-y-1 text-sm text-rose-800 max-h-32 overflow-y-auto">
                    {resultado.erroresMuestra.map((e, i) => (
                      <li key={i}>• {e.nombre} — {e.motivo}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Selector canal */}
              <div className="grid grid-cols-2 gap-2">
                {(["email", "whatsapp"] as Canal[]).map((c) => {
                  const activo = canal === c;
                  return (
                    <button key={c} type="button"
                      onClick={() => setCanal(c)}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-semibold transition ${
                        activo
                          ? c === "email"
                            ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                            : "border-emerald-500 bg-emerald-50 text-emerald-700"
                          : "border-gray-200 bg-white text-gray-500"
                      }`}>
                      {c === "email" ? <Mail className="size-5" /> : <MessageCircle className="size-5" />}
                      {c === "email" ? "Email" : "WhatsApp"}
                    </button>
                  );
                })}
              </div>

              {/* Plantilla */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Plantilla
                  </label>
                  <Link to="/tienda/email-templates" target="_blank"
                    className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                    Editar plantillas <ExternalLink className="size-3" />
                  </Link>
                </div>
                <select value={plantillaTipo}
                  onChange={(e) => {
                    setPlantillaTipo(e.target.value);
                    const p = plantillasFiltradas.find((pl) => pl.tipo === e.target.value);
                    if (p) { setAsunto(p.asunto ?? ""); setCuerpo(p.cuerpo); }
                  }}
                  className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500">
                  {plantillasFiltradas.length === 0 && (
                    <option value="">Cargando plantillas…</option>
                  )}
                  {plantillasFiltradas.map((p) => (
                    <option key={p.tipo} value={p.tipo}>
                      {LABEL_TIPO[p.tipo] ?? p.tipo}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">
                  Las plantillas se gestionan en la sección global de Email Templates.
                </p>
              </div>

              {/* Asunto (solo email) */}
              {canal === "email" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Asunto *
                  </label>
                  <input value={asunto} onChange={(e) => setAsunto(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500"
                    placeholder="Asunto del email" />
                </div>
              )}

              {/* Cuerpo */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Mensaje *
                </label>
                <textarea value={cuerpo} onChange={(e) => setCuerpo(e.target.value)}
                  rows={10}
                  className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500 font-mono text-sm"
                  placeholder="Hola {{primer_nombre}}, …" />
              </div>

              {/* Vars */}
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                <p className="font-semibold mb-1">Variables disponibles (se reemplazan por cliente):</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    "{{nombre}}", "{{primer_nombre}}", "{{tratamiento}}",
                    "{{visitas}}", "{{ticket_promedio}}", "{{gasto_total}}",
                    "{{ultima_visita}}", "{{dias_sin_volver}}",
                  ].map((v) => (
                    <code key={v} className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[11px]">
                      {v}
                    </code>
                  ))}
                </div>
              </div>

              {/* Confirm */}
              {confirmando && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="size-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-semibold mb-1">
                      ¿Confirmás el envío masivo?
                    </p>
                    <p className="text-xs">
                      Se enviará a todos los clientes del segmento que tengan {canal === "email" ? "email" : "teléfono"} cargado. La operación NO se puede revertir.
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <p className="text-rose-600 text-sm">{error}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 rounded-b-2xl flex justify-end gap-2">
          {resultado ? (
            <button onClick={onClose}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition font-semibold">
              Cerrar
            </button>
          ) : (
            <>
              <button onClick={onClose}
                className="px-4 py-2 rounded-xl text-gray-600 hover:bg-gray-200 transition">
                Cancelar
              </button>
              {confirmando ? (
                <button onClick={enviar} disabled={enviando}
                  className="px-4 py-2 rounded-xl bg-rose-600 text-white hover:bg-rose-700 transition disabled:opacity-50 flex items-center gap-2 font-semibold">
                  {enviando && <Loader2 className="size-4 animate-spin" />}
                  {enviando ? "Enviando…" : "Sí, enviar a todos"}
                </button>
              ) : (
                <button onClick={() => setConfirmando(true)}
                  disabled={!cuerpo.trim() || (canal === "email" && !asunto.trim())}
                  className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50 font-semibold">
                  Continuar
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultBox({ label, value, color = "text-gray-800", big = false }: {
  label: string; value: number; color?: string; big?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
      <div className={`font-bold leading-none ${big ? "text-3xl" : "text-2xl"} ${color}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-1 uppercase tracking-wide">{label}</div>
    </div>
  );
}
